import type { LatLon } from './types.ts';
import { angleDiffDeg, bearingDeg, destinationPoint } from './geo.ts';

export interface SensorPreset {
  id: string;
  label: string; // "ARRI ALEXA 35 (Open Gate)"
  widthMm: number;
  heightMm: number;
}

/**
 * Common cinema / mirrorless / phone sensors (active image area for the named mode, from
 * manufacturer spec sheets where published). Good to ~0.1 mm; for planning, not lens charts.
 */
export const SENSOR_PRESETS: readonly SensorPreset[] = Object.freeze([
  { id: 'arri-alexa-35-og', label: 'ARRI ALEXA 35 (4.6K Open Gate)', widthMm: 27.99, heightMm: 19.22 },
  { id: 'arri-alexa-mini-lf-og', label: 'ARRI ALEXA Mini LF (4.5K Open Gate)', widthMm: 36.7, heightMm: 25.54 },
  { id: 'arri-alexa-mini-s35-3.4k', label: 'ARRI ALEXA Mini (S35 3.4K Open Gate)', widthMm: 28.25, heightMm: 18.17 },
  { id: 'red-v-raptor-8k-vv', label: 'RED V-RAPTOR 8K VV', widthMm: 40.96, heightMm: 21.6 },
  { id: 'red-komodo-6k', label: 'RED KOMODO 6K S35', widthMm: 27.03, heightMm: 14.26 },
  { id: 'sony-venice-2-8.6k-ff', label: 'Sony VENICE 2 (8.6K Full Frame 3:2)', widthMm: 36.2, heightMm: 24.1 },
  { id: 'sony-fx6-fx3-ff-16x9', label: 'Sony FX6 / FX3 (Full Frame 16:9)', widthMm: 35.7, heightMm: 18.8 },
  { id: 'canon-c70-s35', label: 'Canon EOS C70 (Super 35 DGO)', widthMm: 26.2, heightMm: 13.8 },
  { id: 'bmd-ursa-mini-pro-12k', label: 'Blackmagic URSA Mini Pro 12K', widthMm: 27.03, heightMm: 14.25 },
  { id: 'bmd-pocket-6k', label: 'Blackmagic Pocket Cinema Camera 6K', widthMm: 23.1, heightMm: 12.99 },
  { id: 'full-frame', label: 'Full frame (36 × 24)', widthMm: 36, heightMm: 24 },
  { id: 'super-35', label: 'Super 35 (4-perf, 24.89 × 18.66)', widthMm: 24.89, heightMm: 18.66 },
  { id: 'mft', label: 'Micro Four Thirds', widthMm: 17.3, heightMm: 13 },
  { id: 'dji-inspire-3-x9-ff', label: 'DJI Inspire 3 — Zenmuse X9 (Full Frame)', widthMm: 35.9, heightMm: 24 },
  { id: 'iphone-16-pro-main', label: 'iPhone 16 Pro (main 48 MP, 1/1.28")', widthMm: 9.8, heightMm: 7.3 },
  { id: 'gopro-1-2.3', label: 'GoPro (1/2.3")', widthMm: 6.17, heightMm: 4.55 },
].map((p) => Object.freeze(p)));

const D2R = Math.PI / 180;

/** Angular field of view (degrees) for a sensor dimension and focal length (rectilinear, focus at ∞). */
export function fovDeg(sensorDimMm: number, focalLengthMm: number): number {
  if (!(sensorDimMm > 0) || !(focalLengthMm > 0)) return NaN;
  return (2 * Math.atan(sensorDimMm / (2 * focalLengthMm))) / D2R;
}

/** Horizontal & vertical FOV; null when inputs are missing. */
export function cameraFov(c: { sensorWidthMm: number | null; sensorHeightMm: number | null; focalLengthMm: number | null }): { hfovDeg: number; vfovDeg: number } | null {
  const { sensorWidthMm: w, sensorHeightMm: h, focalLengthMm: f } = c;
  if (w == null || h == null || f == null || !(w > 0) || !(h > 0) || !(f > 0)) return null;
  return { hfovDeg: fovDeg(w, f), vfovDeg: fovDeg(h, f) };
}

/** Width of scene covered at `distanceM` for a given FOV. */
export function coverageAtDistanceM(fovDegrees: number, distanceM: number): number {
  return 2 * distanceM * Math.tan((fovDegrees * D2R) / 2);
}

/** % of frame width an object of `objectWidthM` fills at `distanceM` (may exceed 100). */
export function frameFillPct(objectWidthM: number, distanceM: number, hfovDeg: number): number {
  if (!(distanceM > 0)) return Infinity;
  const cov = coverageAtDistanceM(hfovDeg, distanceM);
  return cov > 0 ? (objectWidthM / cov) * 100 : Infinity;
}

/** Focal length (mm) that makes an object fill `pct`% of frame width at `distanceM`. */
export function focalForFramePct(objectWidthM: number, distanceM: number, sensorWidthMm: number, pct: number): number {
  const coverage = objectWidthM / (pct / 100);
  return (sensorWidthMm * distanceM) / coverage;
}

/** Wedge polygon (closed ring of [lon,lat]) for map display. Default 24 arc segments. */
export function fovWedge(origin: LatLon, headingDeg: number, hfovDeg: number, rangeM: number, segments = 24): [number, number][] {
  const n = Math.max(1, Math.floor(segments));
  const span = Math.min(360, Math.max(0, hfovDeg));
  const ring: [number, number][] = [[origin.lon, origin.lat]];
  const start = headingDeg - span / 2;
  for (let i = 0; i <= n; i++) {
    const p = destinationPoint(origin, start + (span * i) / n, rangeM);
    ring.push([p.lon, p.lat]);
  }
  ring.push([origin.lon, origin.lat]);
  return ring;
}

/** true if `target` lies within the horizontal FOV of a camera at `origin` looking along `headingDeg`. */
export function isInFov(origin: LatLon, headingDeg: number, hfovDeg: number, target: LatLon): boolean {
  if (hfovDeg >= 360) return true;
  if (origin.lat === target.lat && origin.lon === target.lon) return true;
  return Math.abs(angleDiffDeg(headingDeg, bearingDeg(origin, target))) <= hfovDeg / 2;
}

// ───────────── lenses: prime or zoom (round 4) ─────────────

/** Common zoom ranges for the Setup "Zoom" choice (mm). */
export const ZOOM_PRESETS: ReadonlyArray<{ minMm: number; maxMm: number }> = Object.freeze([
  { minMm: 24, maxMm: 70 },
  { minMm: 70, maxMm: 200 },
  { minMm: 100, maxMm: 400 },
  { minMm: 150, maxMm: 600 },
].map((p) => Object.freeze(p)));

/** Common primes for the Setup "Prime" choice (mm). */
export const PRIME_PRESETS: readonly number[] = Object.freeze([24, 35, 50, 85, 135, 200]);

export interface LensFields {
  focalLengthMm: number | null;
  focalMinMm?: number | null;
  focalMaxMm?: number | null;
}

/** true when the camera has a usable zoom range (min < max, both > 0). */
export function isZoom(c: LensFields): boolean {
  const a = c.focalMinMm;
  const b = c.focalMaxMm;
  return a != null && b != null && a > 0 && b > a;
}

/** "70–200 mm" for a zoom, "85 mm" for a prime, null when nothing is set. */
export function lensRangeText(c: LensFields): string | null {
  if (isZoom(c)) return `${Math.round(c.focalMinMm!)}–${Math.round(c.focalMaxMm!)} mm`;
  return c.focalLengthMm != null && c.focalLengthMm > 0 ? `${Math.round(c.focalLengthMm)} mm` : null;
}

/** Wide and long focal ends (mm): the zoom range, or the prime twice; null when no focal is set. */
export function focalRange(c: LensFields): { minMm: number; maxMm: number } | null {
  if (isZoom(c)) return { minMm: c.focalMinMm!, maxMm: c.focalMaxMm! };
  return c.focalLengthMm != null && c.focalLengthMm > 0 ? { minMm: c.focalLengthMm, maxMm: c.focalLengthMm } : null;
}
